import React, { useRef, useEffect, useState } from 'react';
import { Card, CardContent, Typography, Button, Box, Fade, Stack, Chip, useTheme, IconButton, Grid, Paper, List, ListItem, ListItemText, ListItemAvatar, Avatar, Divider, Container, Slider } from '@mui/material';
import { VolumeUp, VolumeOff, EmojiEvents, Dangerous, MusicNote, GraphicEq } from '@mui/icons-material';
import { createGamificationService } from '../../services/GamificationService';
import quantinoJetpackOn from '../../assets/avatars/quantino_jetpack_on.png';
import quantinoJetpackOff from '../../assets/avatars/quantino_jetpack_off.png';
import quantinoDizzy from '../../assets/avatars/quantino_schwindelig.png';
import gameSoundtrack from '../../assets/sounds/Game song.mp3';
import jumpSoundFile from '../../assets/sounds/thrust.mp3';
import coinSoundFile from '../../assets/sounds/coin.mp3';
import caffeineSoundFile from '../../assets/sounds/caffeine.mp3';
import gameOverSoundFile from '../../assets/sounds/wah-wah-sad-trombone.mp3';

// Import monster images
import mensaMonsterImg from '../../assets/monsters/mensa_monster.png';
import prokrastinatorMonsterImg from '../../assets/monsters/prokrastinator_monster.png';
import signaStoerungMonsterImg from '../../assets/monsters/signa_stoerung_monster.png';
import wgGesuchtMonsterImg from '../../assets/monsters/wg_gesucht_monster.png';
import zitronenSchlitzMonsterImg from '../../assets/monsters/zitronenschlitz_monster.png';
import ectsImg from '../../assets/ects.png';
import koffeinImg from '../../assets/koffein.png';

// Preload Quantino images
const quantinoOnImg = new Image();
quantinoOnImg.src = quantinoJetpackOn;
const quantinoOffImg = new Image();
quantinoOffImg.src = quantinoJetpackOff;
const quantinoDizzyImg = new Image();
quantinoDizzyImg.src = quantinoDizzy;

// Preload Monster images
const mensaMonster = new Image();
mensaMonster.src = mensaMonsterImg;
const prokrastinatorMonster = new Image();
prokrastinatorMonster.src = prokrastinatorMonsterImg;
const signaStoerungMonster = new Image();
signaStoerungMonster.src = signaStoerungMonsterImg;
const wgGesuchtMonster = new Image();
wgGesuchtMonster.src = wgGesuchtMonsterImg;
const zitronenSchlitzMonster = new Image();
zitronenSchlitzMonster.src = zitronenSchlitzMonsterImg;

const ectsItemImg = new Image();
ectsItemImg.src = ectsImg;
const koffeinItemImg = new Image();
koffeinItemImg.src = koffeinImg;

// --- Audio Setup ---
const backgroundMusic = new Audio(gameSoundtrack);
backgroundMusic.loop = true;
backgroundMusic.volume = 0.2;

// Dedicated, looping sound for the jetpack
const jumpSound = new Audio(jumpSoundFile);
jumpSound.loop = true;
jumpSound.volume = 0.15; // Higher base volume

// A function to play short, one-off sound effects reliably
const playEffectSound = (soundFile, volume) => {
  try {
    const sound = new Audio(soundFile);
    sound.volume = volume;
    sound.play();
    return sound; // Return the instance in case we need to manipulate it
  } catch (e) {
    console.warn("Could not play sound effect", e);
    return null;
  }
};

const monsters = [
  {
    name: 'Mensa-Monster',
    comment: 'Das Mensa-Monster hat dich erwischt! Zeit für keine Stärkung!',
    image: mensaMonster,
  },
  {
    name: 'Prokrastinator',
    comment: 'Der Prokrastinator hat zugeschlagen! Morgen ist auch noch ein Tag, oder?',
    image: prokrastinatorMonster,
  },
  {
    name: 'Signal-Störung',
    comment: 'Eine Signal-Störung! Du kommst zu spät..',
    image: signaStoerungMonster,
  },
  {
    name: 'WG-Gesucht-Albtraum',
    comment: 'Der WG-Gesucht-Albtraum hat dich! Die Wohnungssuche geht weiter.',
    image: wgGesuchtMonster,
  },
  {
    name: 'Zitronenschlitz',
    comment: 'Vom Zitronenschchlitz erwischt! Das war wohl nichts.',
    image: zitronenSchlitzMonster,
  },
];

const items = [
  { name: 'ECTS', image: ectsItemImg, value: 1 },
  { name: 'Koffein', image: koffeinItemImg, value: 1 },
];

// Particle system for effects
class Particle {
  constructor(x, y, color) {
    this.x = x;
    this.y = y;
    this.radius = Math.random() * 3 + 2;
    this.color = color;
    this.alpha = 1;
    this.dx = (Math.random() - 0.5) * 3;
    this.dy = Math.random() * -2 - 1;
  }
  update() {
    this.x += this.dx;
    this.y += this.dy;
    this.dy += 0.1;
    this.alpha -= 0.02;
  }
  draw(ctx) {
    ctx.save();
    ctx.globalAlpha = this.alpha;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.radius, 0, 2 * Math.PI);
    ctx.fillStyle = this.color;
    ctx.fill();
    ctx.restore();
  }
}

// Simple Flappy/Jump'n'Run Minigame
export const JumpNRunMinigame = ({ username = 'Student', accessToken }) => {
  const theme = useTheme();
  const canvasRef = useRef(null);
  const musicRef = useRef(backgroundMusic);
  const [running, setRunning] = useState(false);
  const [ectsCount, setEctsCount] = useState(0);
  const [koffeinCount, setKoffeinCount] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [showGameOverOverlay, setShowGameOverOverlay] = useState(false); // New state for overlay
  const [paused, setPaused] = useState(false);
  const [gameOverMonster, setGameOverMonster] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(null);
  const [userCoins, setUserCoins] = useState(null);
  const [isMusicMuted, setIsMusicMuted] = useState(false);
  const [isSoundEffectsMuted, setIsSoundEffectsMuted] = useState(false);
  const [musicVolume, setMusicVolume] = useState(20); // 0-100
  const [soundEffectsVolume, setSoundEffectsVolume] = useState(25); // 0-100
  const [gameOverMessage, setGameOverMessage] = useState('');
  const [initialUnlockDone, setInitialUnlockDone] = useState(false);
  const [gameStarted, setGameStarted] = useState(false); // To track if the game has started at least once

  const toggleMusicMute = () => {
    const newMutedState = !isMusicMuted;
    setIsMusicMuted(newMutedState);
    if (musicRef.current) {
      musicRef.current.muted = newMutedState;
    }
  };

  const toggleSoundEffectsMute = () => {
    const newMutedState = !isSoundEffectsMuted;
    setIsSoundEffectsMuted(newMutedState);
    if (jumpSound) {
      jumpSound.muted = newMutedState;
    }
  };

  const handleMusicVolumeChange = (event, newValue) => {
    setMusicVolume(newValue);
    if (musicRef.current) {
      musicRef.current.volume = newValue / 100;
    }
  };

  const handleSoundEffectsVolumeChange = (event, newValue) => {
    setSoundEffectsVolume(newValue);
    if (jumpSound) {
      jumpSound.volume = (newValue / 100) * 0.15; // Higher base volume for jetpack sound
    }
  };

  const handleRestartClick = async () => {
    if (userCoins < 20) {
      setUnlockError('Du hast nicht genügend Münzen, um erneut zu spielen.');
      return;
    }
    setUnlocking(true);
    setUnlockError(null);
    const gamification = createGamificationService(accessToken);
    try {
      const res = await gamification.unlockMinigame();
      setUserCoins(res.remaining_coins);
      startGame(); // Start game after successful payment
    } catch (e) {
      setUnlockError(e.message || 'Fehler beim Neustart');
    } finally {
      setUnlocking(false);
    }
  };

  // Game state
  const game = useRef({
    playerY: 150,
    velocity: 0,
    gravity: 0.15, // Even slower falling
    thrust: -0.3, // Ultra gentle acceleration
    isThrusting: false,
    items: [],
    monsters: [],
    itemSpeed: 2.0, // Slower initial speed
    monsterSpeed: 2.0, // Slower initial speed
    frame: 0,
    particles: [],
    bgOffset: 0,
    clouds: Array.from({ length: 4 }, (_, i) => ({ x: 100 * i, y: 30 + Math.random() * 40 })),
  });

  // Check unlock status and coins on mount
  useEffect(() => {
    const fetchStatus = async () => {
      if (!accessToken) return;
      const gamification = createGamificationService(accessToken);
      try {
        const stats = await gamification.getUserStats();
        setUserCoins(stats.total_coins);
      } catch (e) {
        setUserCoins(null);
      }
    };
    fetchStatus();
  }, [accessToken]);

  // Ensure music stops on unmount
  useEffect(() => {
    const music = musicRef.current;
    return () => {
      music.pause();
      music.currentTime = 0;
      jumpSound.pause(); // Also stop jetpack sound on unmount
      jumpSound.currentTime = 0;
    };
  }, []);

  // Sync audio mute states with actual audio objects
  useEffect(() => {
    if (musicRef.current) {
      musicRef.current.muted = isMusicMuted;
      musicRef.current.volume = musicVolume / 100;
    }
  }, [isMusicMuted, musicVolume]);

  useEffect(() => {
    if (jumpSound) {
      jumpSound.muted = isSoundEffectsMuted;
      jumpSound.volume = (soundEffectsVolume / 100) * 0.15; // Higher base volume for jetpack
    }
  }, [isSoundEffectsMuted, soundEffectsVolume]);

  // Wrapper function for playing sound effects that respects mute state and volume
  const playGameSoundEffect = (soundFile, baseVolume) => {
    if (!isSoundEffectsMuted) {
      try {
        const sound = new Audio(soundFile);
        // Apply both base volume and slider volume
        sound.volume = baseVolume * (soundEffectsVolume / 100);
        sound.play();
        return sound;
      } catch (e) {
        console.warn("Could not play sound effect", e);
        return null;
      }
    }
    return null;
  };

  // Start/Restart game
  const startGame = () => {
    setEctsCount(0);
    setKoffeinCount(0);
    setGameOver(false);
    setShowGameOverOverlay(false); // Reset overlay state
    setPaused(false);
    setGameOverMonster(null);
    setGameOverMessage('');
    setRunning(true);
    setGameStarted(true); // Mark that the game has started
    if (!isMusicMuted) {
      musicRef.current.muted = false;
      musicRef.current.currentTime = 0;
      musicRef.current.play().catch(e => console.warn("Music playback failed", e));
    }
    game.current = {
      playerY: 150,
      velocity: 0,
      gravity: 0.15, // Even slower falling
      jump: -12,
      thrust: -0.3, // Ultra gentle acceleration
      isThrusting: false,
      items: [],
      monsters: [],
      itemSpeed: 2.0, // Slower initial speed
      monsterSpeed: 2.0, // Slower initial speed
      frame: 0,
      particles: [],
      bgOffset: 0,
      clouds: Array.from({ length: 4 }, (_, i) => ({ x: 100 * i, y: 30 + Math.random() * 40 })),
    };
  };

  // Stop/Pause game
  const stopGame = () => {
    setPaused(true);
    setRunning(false);
  };

  const handleUnlock = async () => {
    setUnlocking(true);
    setUnlockError(null);
    const gamification = createGamificationService(accessToken);
    try {
      const res = await gamification.unlockMinigame();
      setUserCoins(res.remaining_coins);
      setUnlocked(true);
      setInitialUnlockDone(true);
      startGame(); // Start the game immediately after unlocking
    } catch (e) {
      setUnlockError(e.message || 'Fehler beim Freischalten');
    } finally {
      setUnlocking(false);
    }
  };

  // Game loop and input handling
  useEffect(() => {
    // Always run the loop for game over animation
    if (!running && !gameOver) return;
    if (paused) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animation;

    const handleKeyDown = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault(); // Prevent default browser behavior and UI interactions
        if (!game.current.isThrusting) {
          jumpSound.play().catch(e => console.warn("Jetpack sound failed to play", e));
        }
        game.current.isThrusting = true;
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault(); // Prevent default browser behavior and UI interactions
        game.current.isThrusting = false;
        jumpSound.pause();
        jumpSound.currentTime = 0; // Reset for next time
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Parallax background
      game.current.bgOffset = (game.current.bgOffset + 0.5) % canvas.width;
      ctx.fillStyle = '#aee9f9';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Clouds
      ctx.save();
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = '#fff';
      game.current.clouds.forEach(cloud => {
        cloud.x -= 0.3;
        if (cloud.x < -60) cloud.x = canvas.width + Math.random() * 40;
        ctx.beginPath();
        ctx.ellipse(cloud.x - game.current.bgOffset * 0.2, cloud.y, 30, 12, 0, 0, 2 * Math.PI);
        ctx.fill();
      });
      ctx.restore();
      // Hills
      ctx.save();
      ctx.fillStyle = '#b6e388';
      ctx.beginPath();
      ctx.moveTo(0, canvas.height - 30);
      for (let x = 0; x <= canvas.width; x += 40) {
        ctx.lineTo(x, canvas.height - 40 + 10 * Math.sin((x + game.current.bgOffset) / 40));
      }
      ctx.lineTo(canvas.width, canvas.height);
      ctx.lineTo(0, canvas.height);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      // Ground
      ctx.fillStyle = '#7fc97f';
      ctx.fillRect(0, canvas.height - 30, canvas.width, 30);

      // Draw particles, items, and monsters first
      game.current.particles.forEach(p => p.draw(ctx));
      game.current.items.forEach((item, i) => {
        const width = 40;
        const height = 40;
        if (item.image && item.image.complete) {
            ctx.drawImage(item.image, item.x - width / 2, item.y - height / 2, width, height);
        }
      });
      game.current.monsters.forEach((monster) => {
        const width = 60; // Bigger monsters
        const height = 60;
        if (monster.image && monster.image.complete) {
            const wiggleX = Math.sin(game.current.frame * 0.1 + monster.x * 0.01) * 1.5;
            const wiggleY = Math.cos(game.current.frame * 0.12 + monster.x * 0.008) * 1;
            ctx.drawImage(monster.image, monster.x - width / 2 + wiggleX, monster.y - height / 2 + wiggleY, width, height);
        }
      });

      // Player (Quantino) - drawn on top of other game elements
      if (!gameOver) {
        drawQuantino(ctx, 80, game.current.playerY, game.current.isThrusting);
      } else {
        // Draw dizzy quantino and bubble on game over
        const x = 80;
        const y = game.current.playerY;
        const width = 70;
        const height = 70;
        ctx.drawImage(quantinoDizzyImg, x - width / 2, y - height / 2, width, height);
        if (gameOverMessage) {
            const bubbleX = x + width / 2 - 20; // Adjust horizontal position
            const bubbleY = y - 110; // Move bubble even higher
            drawSpeechBubble(ctx, bubbleX, bubbleY, 250, 80, gameOverMessage);
        }
      }

      // --- Physics & Game Logic Update (only if game is running) ---
      if (!gameOver) {
        if (game.current.isThrusting) {
          game.current.velocity += game.current.thrust;
          // Add particles for jetpack effect
          if (game.current.frame % 3 === 0) {
              game.current.particles.push(new Particle(80 - 25, game.current.playerY + 10, '#ffac33'));
          }
        }
        game.current.velocity += game.current.gravity;
        game.current.velocity = Math.max(-8, Math.min(8, game.current.velocity));
        game.current.playerY += game.current.velocity;

        const groundY = canvas.height - 65;
        const ceilingY = 35;
        if (game.current.playerY > groundY) {
          game.current.playerY = groundY;
          game.current.velocity = 0;
        }
        if (game.current.playerY < ceilingY) {
          game.current.playerY = ceilingY;
          game.current.velocity = 0;
        }

        // Item generation
        if (game.current.frame % 110 === 0) {
          const isEcts = Math.random() < 0.7; // 70% chance for ECTS
          const item = isEcts ? items[0] : items[1];
          game.current.items.push({
              x: canvas.width,
              y: 80 + Math.random() * (canvas.height - 180),
              ...item
          });
        }
        game.current.items.forEach(item => (item.x -= game.current.itemSpeed));

        // Monster generation
        if (game.current.frame % 170 === 0) {
          const monsterData = monsters[Math.floor(Math.random() * monsters.length)];
          game.current.monsters.push({
            x: canvas.width,
            y: 80 + Math.random() * (canvas.height - 180),
            ...monsterData,
          });
        }
        game.current.monsters.forEach(monster => (monster.x -= game.current.monsterSpeed));

        const playerBox = { x: 80 - 30, y: game.current.playerY - 30, width: 60, height: 60 };

        // Collision Items
        game.current.items = game.current.items.filter(item => {
          const itemBox = { x: item.x - 15, y: item.y - 15, width: 30, height: 30 };
          if (playerBox.x < itemBox.x + itemBox.width &&
              playerBox.x + playerBox.width > itemBox.x &&
              playerBox.y < itemBox.y + itemBox.height &&
              playerBox.y + playerBox.height > itemBox.y) {
            if (item.name === 'ECTS') {
              setEctsCount(c => c + 1);
              playGameSoundEffect(coinSoundFile, 0.6); // Louder coin sound
            } else if (item.name === 'Koffein') {
              setKoffeinCount(c => c + 1);
              playGameSoundEffect(caffeineSoundFile, 0.6); // Louder caffeine sound
            }
            for (let i = 0; i < 12; i++) {
              game.current.particles.push(new Particle(item.x, item.y, '#ffd200'));
            }
            return false;
          }
          return item.x > -30;
        });

        // Collision Monster
        for (const monster of game.current.monsters) {
          const monsterBox = { x: monster.x - 10, y: monster.y - 10, width: 20, height: 20 }; // Much smaller, fairer hitbox
           if (playerBox.x < monsterBox.x + monsterBox.width &&
              playerBox.x + playerBox.width > monsterBox.x &&
              playerBox.y < monsterBox.y + monsterBox.height &&
              playerBox.y + playerBox.height > monsterBox.y) {
            setGameOver(true);
            setRunning(false);
            setGameOverMonster(monster.name);
            setGameOverMessage(monster.comment);
            musicRef.current.pause(); // Stop background music
            jumpSound.pause(); // Stop jetpack sound
            playGameSoundEffect(gameOverSoundFile, 0.7); // Louder game over sound
            // Delay showing the game over overlay
            setTimeout(() => {
                setShowGameOverOverlay(true);
            }, 3000); // 3-second delay
          }
        }
        game.current.monsters = game.current.monsters.filter(monster => monster.x > -30);

        // Increase speed over time to make it harder
        if (game.current.frame > 0 && game.current.frame % 240 === 0) { // Every 4 seconds (slower)
            const speedIncrease = 0.1; // Even slower acceleration
            const maxSpeed = 6; // Even lower max speed
            game.current.itemSpeed = Math.min(maxSpeed, game.current.itemSpeed + speedIncrease);
            game.current.monsterSpeed = Math.min(maxSpeed, game.current.monsterSpeed + speedIncrease);
        }
      }

      // Update particles regardless of game state for effects
      game.current.particles.forEach(p => p.update());
      game.current.particles = game.current.particles.filter(p => p.alpha > 0);
      game.current.frame++;
      animation = requestAnimationFrame(loop);
    };

    animation = requestAnimationFrame(loop);

    return () => {
        cancelAnimationFrame(animation);
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
    };
  }, [running, paused, gameOver, gameOverMessage, isSoundEffectsMuted, soundEffectsVolume]);

  // This effect is no longer needed, drawing is handled in the main loop
  // useEffect(() => { ... }, [gameOver, gameOverMessage]);

  const drawQuantino = (ctx, x, y, isJumping) => {
    const img = isJumping ? quantinoOnImg : quantinoOffImg;
    const width = 70;
    const height = 70;
    ctx.drawImage(img, x - width / 2, y - height / 2, width, height);
  };

  const drawSpeechBubble = (ctx, x, y, width, height, text) => {
    const radius = 15;
    ctx.fillStyle = 'white';
    ctx.strokeStyle = '#444';
    ctx.lineWidth = 2;

    // Bubble
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.globalAlpha = 0.9;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.stroke();

    // Tail
    ctx.beginPath();
    ctx.moveTo(x + 20, y + height);
    ctx.lineTo(x + 10, y + height + 15);
    ctx.lineTo(x + 40, y + height);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Text
    ctx.fillStyle = '#333';
    ctx.font = 'bold 15px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    const words = text.split(' ');
    let line = '';
    let textY = y + 10;
    const lineHeight = 18;
    const maxWidth = width - 20;

    for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        if (testWidth > maxWidth && n > 0) {
            ctx.fillText(line, x + 10, textY);
            line = words[n] + ' ';
            textY += lineHeight;
        } else {
            line = testLine;
        }
    }
    ctx.fillText(line, x + 10, textY);
  };

  return (
    <Fade in timeout={1000}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          {/* Main Game Content */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ 
              p: 3, 
              height: '100%', 
              boxShadow: 8, 
              borderRadius: 4, 
              background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)', 
              display: 'flex', 
              flexDirection: 'column',
              border: '1px solid rgba(0,0,0,0.08)'
            }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700, 
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Quantino: Jetpack Break
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                      <Chip label={`ECTS: ${ectsCount}`} sx={{ fontWeight: 'bold', background: theme.palette.gamification.silver, color: 'white', fontSize: '0.9rem' }} />
                      <Chip label={`Koffein: ${koffeinCount}`} sx={{ fontWeight: 'bold', background: theme.palette.gamification.bronze, color: 'white', fontSize: '0.9rem' }} />
                      
                      {/* Music Controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: 2, background: 'rgba(0,0,0,0.03)' }}>
                        <IconButton 
                          onClick={toggleMusicMute} 
                          size="small" 
                          disableFocusRipple
                          onMouseDown={(e) => e.preventDefault()}
                          sx={{ 
                            background: 'rgba(0,0,0,0.05)',
                            '&:hover': { background: 'rgba(0,0,0,0.1)' },
                            '&:focus': { outline: 'none' }
                          }}
                          title="Toggle Music"
                        >
                          {isMusicMuted ? <MusicNote sx={{ opacity: 0.5 }} /> : <MusicNote />}
                        </IconButton>
                        <Slider
                          value={musicVolume}
                          onChange={handleMusicVolumeChange}
                          min={0}
                          max={100}
                          size="small"
                          sx={{ width: 60, color: theme.palette.primary.main }}
                          title="Music Volume"
                        />
                      </Box>

                      {/* Sound Effects Controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: 2, background: 'rgba(0,0,0,0.03)' }}>
                        <IconButton 
                          onClick={toggleSoundEffectsMute} 
                          size="small" 
                          disableFocusRipple
                          onMouseDown={(e) => e.preventDefault()}
                          sx={{ 
                            background: 'rgba(0,0,0,0.05)',
                            '&:hover': { background: 'rgba(0,0,0,0.1)' },
                            '&:focus': { outline: 'none' }
                          }}
                          title="Toggle Sound Effects"
                        >
                          {isSoundEffectsMuted ? <GraphicEq sx={{ opacity: 0.5 }} /> : <GraphicEq />}
                        </IconButton>
                        <Slider
                          value={soundEffectsVolume}
                          onChange={handleSoundEffectsVolumeChange}
                          min={0}
                          max={100}
                          size="small"
                          sx={{ width: 60, color: theme.palette.secondary.main }}
                          title="Effects Volume"
                        />
                      </Box>
                  </Stack>
                </Stack>

                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '900 / 400',
                  background: '#333',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: 'inset 0 0 15px rgba(0,0,0,0.6)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexGrow: 1,
                  border: '2px solid rgba(0,0,0,0.1)'
                }}>
                  <canvas
                    ref={canvasRef}
                    width="900"
                    height="400"
                    style={{ width: '100%', height: '100%', display: 'block' }}
                  />
                  {!initialUnlockDone && !gameStarted && (
                    <Box sx={{ 
                      position: 'absolute',
                      textAlign: 'center', 
                      p: 5, 
                      background: 'rgba(0,0,0,0.85)', 
                      borderRadius: 4, 
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      minWidth: '400px',
                      maxWidth: '500px',
                      zIndex: 1
                    }}>
                      <Typography variant="h4" sx={{ mb: 4, color: 'white', fontWeight: 700, textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                        🎮 Unlock Minigame
                      </Typography>
                      
                      <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem', lineHeight: 1.6 }}>
                        Ready for an exciting jetpack adventure? Unlock the game for just 20 coins!
                      </Typography>
                      
                      {/* Coins Display */}
                      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                        <Chip 
                          label={`Coin Balance: ${userCoins ?? '--'}`} 
                          sx={{ 
                            fontWeight: 'bold', 
                            background: theme.palette.gamification.gold, 
                            color: 'white', 
                            fontSize: '1rem',
                            px: 2,
                            py: 1,
                            height: 'auto',
                            '& .MuiChip-label': { px: 2, py: 1 }
                          }} 
                        />
                      </Box>
                      
                      <Button 
                        variant="contained" 
                        size="large"
                        onClick={handleUnlock} 
                        disabled={unlocking || userCoins < 20}
                        sx={{ 
                          px: 6, 
                          py: 2,
                          fontSize: '1.2rem',
                          fontWeight: 700,
                          borderRadius: 3,
                          boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
                          textTransform: 'none',
                          minWidth: 200
                        }}
                      >
                        {unlocking ? 'Unlocking...' : 'Play Now!'}
                      </Button>
                      
                      {unlockError && (
                        <Typography color="error" sx={{ mt: 3, fontSize: '1rem', fontWeight: 500 }}>
                          {unlockError}
                        </Typography>
                      )}
                      
                      {userCoins !== null && userCoins < 20 && (
                        <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.8)', fontSize: '1rem' }}>
                          💰 Not enough coins - you need 20 coins to play
                        </Typography>
                      )}
                    </Box>
                  )}
                  {showGameOverOverlay && (
                    <Fade in timeout={1050}>
                      <Box sx={{
                        position: 'absolute',
                        textAlign: 'center',
                        p: 4,
                        background: 'rgba(0,0,0,0.85)',
                        borderRadius: 3,
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        maxWidth: '90%',
                        zIndex: 1
                      }}>
                        <Typography variant="h3" color="error" sx={{ fontWeight: 'bold', mb: 2, textShadow: '2px 2px 4px #000' }}>
                          Game Over!
                        </Typography>
                        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                            <Chip icon={<img src={ectsImg} alt="ECTS" style={{height: 20, width: 20}} />} label={`${ectsCount} Collected`} sx={{ background: theme.palette.gamification.silver, color: 'white', fontWeight: 'bold' }} />
                            <Chip icon={<img src={koffeinImg} alt="Koffein" style={{height: 20, width: 20}}/>} label={`${koffeinCount} Collected`} sx={{ background: theme.palette.gamification.bronze, color: 'white', fontWeight: 'bold' }} />
                        </Stack>
                        
                        {/* Coins Display */}
                        <Box sx={{ mb: 3 }}>
                          <Chip 
                            label={`Coin Balance: ${userCoins ?? '--'}`} 
                            sx={{ 
                              fontWeight: 'bold', 
                              background: theme.palette.gamification.gold, 
                              color: 'white', 
                              fontSize: '0.9rem'
                            }} 
                          />
                        </Box>
                        
                        <Button
                          variant="contained"
                          onClick={handleRestartClick}
                          disabled={unlocking || userCoins < 20}
                          size="large"
                          sx={{ 
                            borderRadius: 2, 
                            fontWeight: 'bold', 
                            fontSize: '1.1rem', 
                            px: 5, 
                            py: 1.5,
                            boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
                          }}
                        >
                          {unlocking ? 'Starting...' : 'Try again (20 coins)'}
                        </Button>
                        {unlockError && <Typography color="error" sx={{ mt: 2 }}>{unlockError}</Typography>}
                        {userCoins !== null && userCoins < 20 && !unlockError && <Typography sx={{ mt: 1, color: 'white', fontSize: '0.9rem' }}>Not enough coins.</Typography>}
                      </Box>
                    </Fade>
                  )}
                </Box>
                <Typography variant="body2" sx={{ 
                  display: 'block', 
                  textAlign: 'center', 
                  mt: 3, 
                  color: 'text.secondary',
                  background: 'rgba(0,0,0,0.03)',
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.05)'
                }}>
                  🎮 <strong>Controls:</strong> Thrust the jetpack with <Chip component="span" label="Space" size="small" sx={{ mx: 0.5 }} /> or <Chip component="span" label="↑" size="small" sx={{ mx: 0.5 }} /> to collect items! Avoid the monsters!
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Info Box */}
          <Grid item xs={12} lg={4}>
              <Paper elevation={4} sx={{ 
                p: 3, 
                borderRadius: 4, 
                height: '100%', 
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', 
                display: 'flex', 
                flexDirection: 'column',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700, 
                    mb: 3, 
                    color: theme.palette.primary.dark, 
                    textAlign: 'center',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    🎯 Game Info
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
                      <EmojiEvents sx={{mr: 1, color: 'gold'}}/> Rewards
                    </Typography>
                    <List dense>
                        {items.map(item => (
                            <ListItem key={item.name} sx={{ 
                              background: 'rgba(255,215,0,0.1)', 
                              borderRadius: 2, 
                              mb: 1,
                              border: '1px solid rgba(255,215,0,0.2)'
                            }}>
                                <ListItemAvatar><Avatar src={item.image.src} sx={{ background: 'transparent' }} /></ListItemAvatar>
                                <ListItemText primary={item.name} primaryTypographyProps={{fontWeight: '600'}} />
                            </ListItem>
                        ))}
                    </List>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Dangerous sx={{mr: 1, color: 'red'}}/> Enemies
                    </Typography>
                    <List dense>
                        {monsters.map(monster => (
                            <ListItem key={monster.name} sx={{ 
                              background: 'rgba(244,67,54,0.1)', 
                              borderRadius: 2, 
                              mb: 1,
                              border: '1px solid rgba(244,67,54,0.2)'
                            }}>
                                <ListItemAvatar><Avatar src={monster.image.src} sx={{ background: 'transparent' }} /></ListItemAvatar>
                                <ListItemText primary={monster.name} primaryTypographyProps={{fontWeight: '600'}} />
                            </ListItem>
                        ))}
                    </List>
                  </Box>
              </Paper>
          </Grid>
        </Grid>
      </Container>
    </Fade>
  );
};